import DateUtils from "src/services/utils/DateUtils";
import {
    HiChevronDoubleDown,
    HiChevronDoubleUp,
    HiMenuAlt4
} from "react-icons/hi";
import { 
    INTERNATIONAL_DATE_FORMAT,
} from "src/common/constants"; 
import { Button, UncontrolledTooltip } from "reactstrap";

const MailSettingsKeysTitles = {
    'emailAddress': 'From Email Address',
    'displayName': 'Sender Name',
    'smtpServer': 'SMTP Server',
    'smtpPort': 'SMTP Port',
    'smtpEncryption': 'Encryption Method',
    'smtpUsername': 'SMTP Username',
    'authModeOut': 'Authentication Mode'
};

const dateUtils = new DateUtils();

export const SupplierStatuses = {
    "new"   :   {
        title   :   "New",
        color   :   "bg-secondary"
    },
    "under review"    :   {
        title   :   "Under Review",
        color   :   "bg-info"
    },
    "approved"  :   {
        title   :   "Approved",
        color   :   "bg-success"
    },
    "rejected"  :   {
        title   :   "Rejected",
        color   :   "bg-danger"
    }
}

export const taskIdGenerator = ({ task }) => {
    const isOwnCompany = task.organization?.isOwnCompany;
    const isMitTask = task.relatedRisk?.secondary_id;
    const supplierId = task.organization?.secondaryId || task.supplier;
    const taskId = task.secondary_id;
    
    if (isMitTask) {
        return `${isOwnCompany ? 'E' : 'TP'}${supplierId}/R${task.relatedRisk.secondary_id}/T${taskId}`
    }

    return `${isOwnCompany ? 'E' : 'TP'}${supplierId}/T${task.secondary_id}`
}

export const riskIdGenerator = ({ risk }) => {
    const supplierId = risk?.organization?.secondaryId || risk?.supplierId

    if (!supplierId) return `R${risk?.secondaryId}` // general risk

    return `${risk?.organization?.isOwnCompany ? 'E' : 'TP'}${supplierId}/R${risk?.secondaryId}`
}

export const ActionsMetaData = {
    admin: {
        delete_user: [
            {
                key: "deleted_user_name",
                title: "Deleted User" 
            }
        ],
        edit_user: [
            {
                key: "edited_user_name",
                title: "Edited User" 
            },
            {
                key: "changes",
                title: "Changes",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, t }) => {
                    if (!value || typeof value !== 'object' || Array.isArray(value)) {
                        return undefined;
                    }

                    const fieldLabels = {
                        phoneNumber: t('Office Number'),
                        mobileNumber: t('Mobile Number'),
                    };

                    return (
                        <div className="d-flex justify-content-center align-items-start flex-column">
                            {Object.entries(value).map(([key, change]) => (
                                <p className="mb-2" key={key}>
                                    <i className="ri-checkbox-blank-circle-fill me-1 align-middle text-primary font-size-12" />
                                    {fieldLabels[key] || t(key)}
                                    {' >> '}
                                    {t('Before')}: {change?.from ?? '-'}
                                    {' | '}
                                    {t('After')}: {change?.changed_to ?? change?.to ?? '-'}
                                </p>
                            ))}
                        </div>
                    );
                }
            }
        ],
        create_user: [
            {
                key: "created_users",
                title: "Created Users",
                valueRenderer: ({ value }) => {
                    return (
                        <div className="d-flex justify-content-center align-items-start flex-column">
                            {(value || []).map((user) => {
                                return (
                                    <p className="mb-0 d-flex justify-content-center align-items-center">
                                        <i className="ri-check-line me-1" />
                                        {user.name}
                                    </p>
                                )
                            })}
                        </div>
                    )
                }
            }
        ],
        remove_tag: [
            {
                key: 'removed_tag',
                title: 'Removed Keyword',
                valueRenderer: ({ value, t }) => {
                    return t(value)
                },
                hideIfValueIsUndefined: true
            }
        ],
        add_tag: [
            {
                key: 'added_tag',
                title: 'Added Keyword',
                valueRenderer: ({ value, t }) => {
                    return t(value)
                },
                hideIfValueIsUndefined: true
            }
        ],
        edit_mailer_setting: [
            {
                key: "changes",
                title: "Changes",
                valueRenderer: ({ value, t }) => {
                    return (
                        <div className="d-flex justify-content-center align-items-start flex-column">
                            {(value || []).map((item) => {
                                return (
                                    <p className="mb-2">
                                        <i className="ri-checkbox-blank-circle-fill me-1 align-middle text-primary font-size-12" />
                                        {t(MailSettingsKeysTitles[item.field])} {'>>'} {t('Before')}: {item.old_value} | {t('After')}: {item.new_value}
                                    </p>
                                )
                            })}
                        </div>
                    )
                }
            }
        ],
        toggle_mailer_setting_active: [
            {
                key: "previous_status",
                title: "Previous Status",
                valueRenderer: ({ value, t }) => {
                    return t(value ? 'Active' : 'Inactive')
                }
            },
            {
                key: "new_status",
                title: "New Status",
                valueRenderer: ({ value, t }) => {
                    return t(value ? 'Active' : 'Inactive')
                }
            }
        ]
    },
    entityManagement: {
        edit_routing: [
            {
                key: "default_supplier_owner",
                title: "Default owner"
            },
            {
                key: "default_supplier_risk_owner",
                title: "Assign risks to"
            },
            {
                key: "default_supplier_task_owner",
                title: "Assign tasks to"
            }
        ],
    
        edit_financial_setting: [
            {
                key: "financial_year_start",
                title: "Start of Financial Year",
                valueRenderer: ({ value }) => {
                    return dateUtils.convertDateToDate(
                        value,
                        INTERNATIONAL_DATE_FORMAT
                    )
                }
            },
            {
                key: "financial_year_end",
                title: "End of Financial Year",
                valueRenderer: ({ value }) => {
                    return dateUtils.convertDateToDate(
                        value,
                        INTERNATIONAL_DATE_FORMAT
                    )
                }
            }
        ],

        add_institute: [
            {
                key: 'institute_title',
                title: 'Institute Title'
            },
            {
                key: 'institute_id',
                title: 'Institute Id'
            },
            {
                key: 'legalEntityTitle',
                title: 'Legal Entity Title'
            }
        ],
        edit_institute: [
            {
                key: 'institute_title',
                title: 'Institute Title'
            },
            {
                key: 'institute_id',
                title: 'Institute Id'
            },
            {
                key: 'legalEntityTitle',
                title: 'Legal Entity Title'
            }
        ],
        remove_institute: [
            {
                key: 'institute_title',
                title: 'Institute Title'
            },
            {
                key: 'institute_id',
                title: 'Institute Id'
            },
            {
                key: 'legalEntityTitle',
                title: 'Legal Entity Title'
            }
        ],
    
        add_division: [
            {
                key: 'division_title',
                title: 'Department Title'
            },
            {
                key: 'division_id',
                title: 'Department Id'
            },
            {
                key: 'institute',
                title: 'Institute Title'
            },
            {
                key: 'legal_entity',
                title: 'Legal Entity'
            }
        ],
        edit_division: [
            {
                key: 'institute',
                title: 'Institute Title'
            },
            {
                key: 'legal_entity',
                title: 'Legal Entity'
            },
            {
                key: 'division_id',
                title: 'Department Id'
            },
            {
                key: 'old_title',
                title: 'Previous Title'
            },
            {
                key: 'new_title',
                title: 'Updated Title'
            }
        ],
        remove_division: [
            {
                key: 'institute',
                title: 'Institute Title'
            },
            {
                key: 'legal_entity',
                title: 'Legal Entity'
            },
            {
                key: 'division_id',
                title: 'Department Id'
            },
            {
                key: 'division_title',
                title: 'Department Title'
            }
        ],
    
        add_legal_entity: [
            {
                key: 'legal_entity_title',
                title: 'Legal Entity Title'
            },
            {
                key: 'short_name',
                title: 'Short Name'
            },
            {
                key: 'legal_entity_id',
                title: 'Legal Entity Id'
            }
        ],
        edit_legal_entity: [
            {
                key: 'legal_entity_id',
                title: 'Legal Entity Id'
            },
            {
                key: 'legal_entity_title',
                title: 'Legal Entity Title'
            }
        ],
        remove_legal_entity: [
            {
                key: 'legal_entity_title',
                title: 'Legal Entity Title'
            }
        ],
        delete: [
            {
                key: 'legal_entity_id',
                title: 'Legal Entity Id'
            },
            {
                key: 'legal_entity_title',
                title: 'Legal Entity Title'
            }
        ],
        force_delete: [
            {
                key: 'legal_entity_id',
                title: 'Legal Entity Id'
            },
            {
                key: 'legal_entity_title',
                title: 'Legal Entity Title'
            }
        ],
        add_legal_location: [
            {
                key: 'address',
                title: 'Address'
            },
            {
                key: 'location_id',
                title: 'Location Id'
            }
        ],
        edit_legal_location: [
            {
                key: 'address',
                title: 'Address'
            },
            {
                key: 'location_id',
                title: 'Location Id'
            }
        ],
        remove_legal_location: [
            {
                key: 'address',
                title: 'Address'
            },
            {
                key: 'location_id',
                title: 'Location Id'
            }
        ]
    },
    supplier: {
        add_connected_legal: [
            {
                key: "connected_legal_name",
                title: "Connected Legal Name"
            },
            {
                key: "financial_year",
                title: "Financial Year"
            },
            {
                key: "total_revenue",
                title: "Total Revenue"
            }
        ],
        delete_connected_legal: [
            {
                key: "connected_legal_name",
                title: "Connected Legal Name"
            },
            {
                key: "financial_year",
                title: "Financial Year"
            },
            {
                key: "total_revenue",
                title: "Total Revenue"
            }
        ],
    
        add_share_holder: [
            {
                key: "type",
                title: "Type",
                valueRenderer: ({ value, t }) => {
                    return t(value === 'company' ? 'Company' : 'Individual')
                }
            },
            {
                key: "company_name",
                title: "Shareholder Name",
                valueRenderer: ({ value, otherFields }) => {
                    return otherFields?.type === 'company' ? value : `${otherFields?.first_name} ${otherFields?.last_name}`
                }
            },
            {
                key: "percentage",
                title: "Percentage",
                valueRenderer: ({ value }) => {
                    return `${value}%`
                }
            }
        ],
        edit_share_holder: [
            {
                key: "type",
                title: "Type",
                valueRenderer: ({ value, t }) => {
                    return t(value === 'company' ? 'Company' : 'Individual')
                }
            },
            {
                key: "company_name",
                title: "Shareholder Name",
                valueRenderer: ({ value, otherFields }) => {
                    return otherFields?.type === 'company' ? value : `${otherFields?.first_name} ${otherFields?.last_name}`
                }
            },
            {
                key: "percentage",
                title: "Percentage",
                valueRenderer: ({ value }) => {
                    return `${value}%`
                }
            }
        ],
        delete_share_holder: [
            {
                key: "type",
                title: "Type",
                valueRenderer: ({ value, t }) => {
                    return t(value === 'company' ? 'Company' : 'Individual')
                }
            },
            {
                key: "company_name",
                title: "Shareholder Name",
                valueRenderer: ({ value, otherFields }) => {
                    return otherFields?.type === 'company' ? value : `${otherFields?.first_name} ${otherFields?.last_name}`
                }
            },
            {
                key: "percentage",
                title: "Percentage",
                valueRenderer: ({ value }) => {
                    return `${value}%`
                }
            }
        ],
    
        add_product: [
            {
                key: 'product_name',
                title: 'Product Name'
            },
            {
                key: 'description',
                title: 'Product Description'
            }
        ],
        edit_product: [
            {
                key: 'product_name',
                title: 'Product Name'
            },
            {
                key: 'description',
                title: 'Product Description'
            }
        ],
        delete_product: [
            {
                key: 'product_name',
                title: 'Product Name'
            }
        ],
    
        add_team_manager: [
            {
                key: 'team_manager_title',
                title: 'Salutation'
            },
            {
                key: 'team_manager_name',
                title: 'Person Name'
            },
            {
                key: 'team_manager_email',
                title: 'Email'
            },
            {
                key: 'team_manager_position',
                title: 'Position'
            }
        ],
        edit_team_manager: [
            {
                key: 'team_manager_title',
                title: 'Salutation'
            },
            {
                key: 'team_manager_name',
                title: 'Person Name'
            },
            {
                key: 'team_manager_email',
                title: 'Email'
            },
            {
                key: 'team_manager_position',
                title: 'Position'
            }
        ],
        delete_team_manager: [
            {
                key: 'team_maanger_title',
                title: 'Salutation'
            },
            {
                key: 'team_maanger_name',
                title: 'Person Name'
            },
            {
                key: 'team_maanger_email',
                title: 'Email'
            },
            {
                key: 'team_maanger_position',
                title: 'Position'
            }
        ],

        create_supplier_location: [
            {
                key: 'type',
                title: 'Type',
                valueRenderer: ({ value, t }) => {
                    return t(value === 'office' ? 'Office' : 'Factory')
                }
            },
            {
                key: 'country',
                title: 'Country'
            },
            {
                key: 'postal_code',
                title: 'Postal Code'
            },
            {
                key: 'city',
                title: 'City'
            },
            {
                key: 'address',
                title: 'Street, Number'
            }
        ],
        edit_supplier_location: [
            {
                key: 'type',
                title: 'Type',
                valueRenderer: ({ value, t }) => {
                    return t(value === 'office' ? 'Office' : 'Factory')
                }
            },
            {
                key: 'country',
                title: 'Country'
            },
            {
                key: 'postal_code',
                title: 'Postal Code'
            },
            {
                key: 'city',
                title: 'City'
            },
            {
                key: 'address',
                title: 'Street, Number'
            }
        ],
        delete_supplier_location: [
            {
                key: 'type',
                title: 'Type',
                valueRenderer: ({ value, t }) => {
                    return t(value === 'office' ? 'Office' : 'Factory')
                }
            },
            {
                key: 'country',
                title: 'Country'
            },
            {
                key: 'postal_code',
                title: 'Postal Code'
            },
            {
                key: 'city',
                title: 'City'
            },
            {
                key: 'address',
                title: 'Street, Number'
            }
        ],
    
        add_contact_person: [
            {
                key: 'contact_person_title',
                title: 'Salutation'
            },
            {
                key: 'contact_person_name',
                title: 'Person Name',
                valueRenderer: ({ otherFields }) => {
                    return `${otherFields?.contact_person_name} ${otherFields?.contact_person_lastName}`
                }
            },
            {
                key: 'contact_person_email',
                title: 'Email'
            },
            {
                key: 'contact_person_phoneNumber',
                title: 'Phone Number'
            }
        ],
        edit_contact_person: [
            {
                key: 'contact_person_title',
                title: 'Salutation'
            },
            {
                key: 'contact_person_name',
                title: 'Person Name',
                valueRenderer: ({ otherFields }) => {
                    return `${otherFields?.contact_person_name} ${otherFields?.contact_person_lastName}`
                }
            },
            {
                key: 'contact_person_email',
                title: 'Email'
            },
            {
                key: 'contact_person_phoneNumber',
                title: 'Phone Number'
            }
        ],
        delete_contact_person: [
            {
                key: 'contact_person_salutation',
                title: 'Salutation'
            },
            {
                key: 'contact_person_name',
                title: 'Person Name',
                valueRenderer: ({ otherFields }) => {
                    return `${otherFields?.contact_person_name} ${otherFields?.contact_person_lastName}`
                }
            },
            {
                key: 'contact_person_email',
                title: 'Email'
            },
            {
                key: 'contact_person_phone',
                title: 'Phone Number'
            }
        ],
    
        add_legal_location: [
            {
                key: 'address',
                title: 'Address'
            }
        ],
        edit_legal_location: [
            {
                key: 'address',
                title: 'Address'
            }
        ],
        remove_legal_location: [
            {
                key: 'address',
                title: 'Address'
            }
        ],

        update_supplier_status: [
            {
                key: 'old_status',
                title: 'Previous Status',
                valueRenderer: ({ value, t }) => {
                    return t(SupplierStatuses[value]?.title)
                }
            },
            {
                key: 'status',
                title: 'New Status',
                valueRenderer: ({ value, t }) => {
                    return t(SupplierStatuses[value]?.title)
                }
            }
        ],
        edit_supplier_analyst_admin: [
            {
                key: 'analyst_admin',
                title: 'Assigned Analyst'
            }
        ],
        supplier_toggle_flag: [
            {
                key: 'flag',
                title: 'Flag Status',
                valueRenderer: ({ value, t }) => {
                    return t(value ? 'True' : 'False')
                }
            }
        ],
        supplier_finding_toggle_flag: [
            {
                key: 'flag',
                title: 'Flag Status',
                valueRenderer: ({ value, t }) => {
                    return t(value ? 'True' : 'False')
                }
            }
        ],

        create_report_request: [
            {
                key: 'report_request_id',
                title: 'Report ID'
            },
            {
                key: 'responsible_person_name',
                title: 'Contact Person'
            },
            {
                key: 'deadline_date',
                title: 'Deadline'
            },
            {
                key: 'questionnaire_name',
                title: 'Report Type'
            }
        ],
        withdraw_report_request: [
            {
                key: 'report_request_id',
                title: 'Report ID'
            },
            {
                key: 'responsible_person_name',
                title: 'Contact Person'
            },
            {
                key: 'deadline_date',
                title: 'Deadline'
            },
            {
                key: 'questionnaire_name',
                title: 'Report Type'
            }
        ],

        supplier_finding_toggle_read_flag: [
            {
                key: 'flag',
                title: 'New Status',
                valueRenderer: ({ value, t }) => {
                    return t(value ? 'Read' : 'Unread')
                }
            }
        ],

        edit_supplier_information: [
            // -------------------------
            // Managers
            // -------------------------
            {
              key: "managers_added",
              title: "Managers added",
              hideIfValueIsUndefined: true,
              valueRenderer: ({ value }) => {
                if (!Array.isArray(value) || value.length === 0) return "";
            
                return (
                  <>
                    {value.map((m, idx) => (
                      <span key={m?.id ?? idx} className="d-block">
                        {m?.title ? `${m.title} ` : ""}
                        {m?.name || ""}
                        {m?.position ? `, ${m.position}` : ""}
                        {m?.language ? `, ${m.language}` : ""}
                        {m?.email ? `, ${m.email}` : ""}
                      </span>
                    ))}
                  </>
                );
              }
            },
            {
              key: "managers_updated",
              title: "Managers updated",
              hideIfValueIsUndefined: true,
              valueRenderer: ({ value, t }) => {
                if (!Array.isArray(value) || value.length === 0) return "";
            
                return (
                  <>
                    {value.map((item, idx) => (
                      <span key={idx} className="d-block">
                        <b>{t("Before")}:</b>{" "}
                        {(item?.previousValues?.title ? `${item.previousValues.title} ` : "")}
                        {item?.previousValues?.name || ""}
                        {item?.previousValues?.position ? `, ${item.previousValues.position}` : ""}
                        {" → "}
                        <b>{t("After")}:</b>{" "}
                        {(item?.newValues?.title ? `${item.newValues.title} ` : "")}
                        {item?.newValues?.name || ""}
                        {item?.newValues?.position ? `, ${item.newValues.position}` : ""}
                      </span>
                    ))}
                  </>
                );
              }
            },
            {
              key: "managers_removed",
              title: "Managers removed",
              hideIfValueIsUndefined: true,
              valueRenderer: ({ value }) => {
                if (!Array.isArray(value) || value.length === 0) return "";
            
                return (
                  <>
                    {value.map((m, idx) => (
                      <span key={m?.id ?? idx} className="d-block">
                        {m?.title ? `${m.title} ` : ""}
                        {m?.name || ""}
                        {m?.position ? `, ${m.position}` : ""}
                        {m?.language ? `, ${m.language}` : ""}
                        {m?.email ? `, ${m.email}` : ""}
                      </span>
                    ))}
                  </>
                );
              }
            },
          
            // -------------------------
            // Shareholders
            // -------------------------
            {
              key: "shareholders_added",
              title: "Shareholders added",
              hideIfValueIsUndefined: true,
              valueRenderer: ({ value, t }) => {
                if (!Array.isArray(value) || value.length === 0) return "";
            
                return (
                  <>
                    {value.map((s, idx) => (
                      <span key={s?.id ?? idx} className="d-block">
                        {s?.type === "company"
                          ? (s?.companyName || "")
                          : ([s?.title, s?.firstName, s?.lastName].filter(Boolean).join(" ") || "-")}
                        {typeof s?.percentage === "number" ? `, ${s.percentage}%` : ""}
                        {s?.isUbo ? ", UBO" : ""}
                      </span>
                    ))}
                  </>
                );
              }
            },
            {
              key: "shareholders_updated",
              title: "Shareholders updated",
              hideIfValueIsUndefined: true,
              valueRenderer: ({ value, t }) => {
                if (!Array.isArray(value) || value.length === 0) return "";
            
                return (
                  <>
                    {value.map((item, idx) => (
                      <span key={idx} className="d-block">
                        <b>{t("Before")}:</b>{" "}
                        {(item?.previousValues?.type === "company"
                          ? item?.previousValues?.companyName
                          : [item?.previousValues?.title, item?.previousValues?.firstName, item?.previousValues?.lastName].filter(Boolean).join(" ")
                        ) || ""}
                        {" → "}
                        <b>{t("After")}:</b>{" "}
                        {(item?.newValues?.type === "company"
                          ? item?.newValues?.companyName
                          : [item?.newValues?.title, item?.newValues?.firstName, item?.newValues?.lastName].filter(Boolean).join(" ")
                        ) || ""}
                      </span>
                    ))}
                  </>
                );
              }
            },
            {
              key: "shareholders_removed",
              title: "Shareholders removed",
              hideIfValueIsUndefined: true,
              valueRenderer: ({ value }) => {
                if (!Array.isArray(value) || value.length === 0) return "";
            
                return (
                  <>
                    {value.map((s, idx) => (
                      <span key={s?.id ?? idx} className="d-block">
                        {s?.type === "company"
                          ? (s?.companyName || s?.localLanguageCompanyName || "")
                          : ([s?.title, s?.firstName, s?.lastName].filter(Boolean).join(" ") || "")}
                        {typeof s?.percentage === "number" ? `, ${s.percentage}%` : ""}
                        {s?.isUbo ? ", UBO" : ""}
                      </span>
                    ))}
                  </>
                );
              }
            },
          
            // -------------------------
            // Locations
            // -------------------------
            {
              key: "locations_added",
              title: "Locations added",
              hideIfValueIsUndefined: true,
            valueRenderer: ({ value }) => {
                if (!Array.isArray(value) || value.length === 0) return "";
            
                return (
                  <>
                    {value.map((l, idx) => (
                      <span key={idx} className="d-block">
                        {l?.country}, {l?.city}, {l?.postalCode}, {l?.address}
                      </span>
                    ))}
                  </>
                );
              }
            },
            {
              key: "locations_updated",
              title: "Locations updated",
              hideIfValueIsUndefined: true,
              valueRenderer: ({ value, t }) => {
                
                if (Array.isArray(value)) {
                    return (
                        <>
                        {value.map((item, idx) => (
                            <span key={idx} className="d-block mb-1">
                                <b>{t("Before")}:</b>{" "}
                                {item?.previousValues?.country || ""},{" "}
                                {item?.previousValues?.city || ""},{" "}
                                {item?.previousValues?.postalCode || ""},{" "}
                                {item?.previousValues?.address || ""}
                                <br />
                                <b>{t("After")}:</b>{" "}
                                {item?.newValues?.country || ""},{" "}
                                {item?.newValues?.city || ""},{" "}
                                {item?.newValues?.postalCode || ""},{" "}
                                {item?.newValues?.address || ""}
                            </span>
                        ))}
                    </>

                    )
                }
                
              }
            },
            {
              key: "locations_removed",
              title: "Locations removed",
              hideIfValueIsUndefined: true,
              valueRenderer: ({ value }) => {
                if (!Array.isArray(value) || value.length === 0) return "";
                return (
                  <>
                    {value.map((l, idx) => (
                      <span key={idx} className="d-block">
                        {l?.country}, {l?.city}, {l?.postalCode}, {l?.address}
                      </span>
                    ))}
                  </>
                );
              }
            },
          ],
          
          
          
    },
    ownCompany: {
        add_connected_legal: [
            {
                key: "connected_legal_name",
                title: "Connected Legal Name"
            },
            {
                key: "financial_year",
                title: "Financial Year"
            },
            {
                key: "total_revenue",
                title: "Total Revenue"
            }
        ],
        delete_connected_legal: [
            {
                key: "connected_legal_name",
                title: "Connected Legal Name"
            },
            {
                key: "financial_year",
                title: "Financial Year"
            },
            {
                key: "total_revenue",
                title: "Total Revenue"
            }
        ],
    
        add_share_holder: [
            {
                key: "type",
                title: "Type",
                valueRenderer: ({ value, t }) => {
                    return t(value === 'company' ? 'Company' : 'Individual')
                }
            },
            {
                key: "company_name",
                title: "Shareholder Name",
                valueRenderer: ({ value, otherFields }) => {
                    return otherFields?.type === 'company' ? value : `${otherFields?.first_name} ${otherFields?.last_name}`
                }
            },
            {
                key: "percentage",
                title: "Percentage",
                valueRenderer: ({ value }) => {
                    return `${value}%`
                }
            }
        ],
        edit_share_holder: [
            {
                key: "type",
                title: "Type",
                valueRenderer: ({ value, t }) => {
                    return t(value === 'company' ? 'Company' : 'Individual')
                }
            },
            {
                key: "company_name",
                title: "Shareholder Name",
                valueRenderer: ({ value, otherFields }) => {
                    return otherFields?.type === 'company' ? value : `${otherFields?.first_name} ${otherFields?.last_name}`
                }
            },
            {
                key: "percentage",
                title: "Percentage",
                valueRenderer: ({ value }) => {
                    return `${value}%`
                }
            }
        ],
        delete_share_holder: [
            {
                key: "type",
                title: "Type",
                valueRenderer: ({ value, t }) => {
                    return t(value === 'company' ? 'Company' : 'Individual')
                }
            },
            {
                key: "company_name",
                title: "Shareholder Name",
                valueRenderer: ({ value, otherFields }) => {
                    return otherFields?.type === 'company' ? value : `${otherFields?.first_name} ${otherFields?.last_name}`
                }
            },
            {
                key: "percentage",
                title: "Percentage",
                valueRenderer: ({ value }) => {
                    return `${value}%`
                }
            }
        ],
    
        add_product: [
            {
                key: 'product_name',
                title: 'Product Name'
            },
            {
                key: 'description',
                title: 'Product Description'
            }
        ],
        edit_product: [
            {
                key: 'product_name',
                title: 'Product Name'
            },
            {
                key: 'description',
                title: 'Product Description'
            }
        ],
        delete_product: [
            {
                key: 'product_name',
                title: 'Product Name'
            }
        ],
    
        add_team_manager: [
            {
                key: 'team_manager_title',
                title: 'Salutation'
            },
            {
                key: 'team_manager_name',
                title: 'Person Name'
            },
            {
                key: 'team_manager_email',
                title: 'Email'
            },
            {
                key: 'team_manager_position',
                title: 'Position'
            }
        ],
        edit_team_manager: [
            {
                key: 'team_manager_title',
                title: 'Salutation'
            },
            {
                key: 'team_manager_name',
                title: 'Person Name'
            },
            {
                key: 'team_manager_email',
                title: 'Email'
            },
            {
                key: 'team_manager_position',
                title: 'Position'
            }
        ],
        delete_team_manager: [
            {
                key: 'team_maanger_title',
                title: 'Salutation'
            },
            {
                key: 'team_maanger_name',
                title: 'Person Name'
            },
            {
                key: 'team_maanger_email',
                title: 'Email'
            },
            {
                key: 'team_maanger_position',
                title: 'Position'
            }
        ],
    
        create_supplier_location: [
            {
                key: 'type',
                title: 'Type',
                valueRenderer: ({ value, t }) => {
                    return t(value === 'office' ? 'Office' : 'Factory')
                }
            },
            {
                key: 'country',
                title: 'Country'
            },
            {
                key: 'postal_code',
                title: 'Postal Code'
            },
            {
                key: 'city',
                title: 'City'
            },
            {
                key: 'address',
                title: 'Street, Number'
            }
        ],
        edit_supplier_location: [
            {
                key: 'type',
                title: 'Type',
                valueRenderer: ({ value, t }) => {
                    return t(value === 'office' ? 'Office' : 'Factory')
                }
            },
            {
                key: 'country',
                title: 'Country'
            },
            {
                key: 'postal_code',
                title: 'Postal Code'
            },
            {
                key: 'city',
                title: 'City'
            },
            {
                key: 'address',
                title: 'Street, Number'
            }
        ],
        delete_supplier_location: [
            {
                key: 'type',
                title: 'Type',
                valueRenderer: ({ value, t }) => {
                    return t(value === 'office' ? 'Office' : 'Factory')
                }
            },
            {
                key: 'country',
                title: 'Country'
            },
            {
                key: 'postal_code',
                title: 'Postal Code'
            },
            {
                key: 'city',
                title: 'City'
            },
            {
                key: 'address',
                title: 'Street, Number'
            }
        ],
    
        add_contact_person: [
            {
                key: 'contact_person_title',
                title: 'Salutation'
            },
            {
                key: 'contact_person_name',
                title: 'Person Name',
                valueRenderer: ({ otherFields }) => {
                    return `${otherFields?.contact_person_name} ${otherFields?.contact_person_lastName}`
                }
            },
            {
                key: 'contact_person_email',
                title: 'Email'
            },
            {
                key: 'contact_person_phoneNumber',
                title: 'Phone Number'
            }
        ],
        edit_contact_person: [
            {
                key: 'contact_person_title',
                title: 'Salutation'
            },
            {
                key: 'contact_person_name',
                title: 'Person Name',
                valueRenderer: ({ otherFields }) => {
                    return `${otherFields?.contact_person_name} ${otherFields?.contact_person_lastName}`
                }
            },
            {
                key: 'contact_person_email',
                title: 'Email'
            },
            {
                key: 'contact_person_phoneNumber',
                title: 'Phone Number'
            }
        ],
        delete_contact_person: [
            {
                key: 'contact_person_salutation',
                title: 'Salutation'
            },
            {
                key: 'contact_person_name',
                title: 'Person Name',
                valueRenderer: ({ otherFields }) => {
                    return `${otherFields?.contact_person_name} ${otherFields?.contact_person_lastName}`
                }
            },
            {
                key: 'contact_person_email',
                title: 'Email'
            },
            {
                key: 'contact_person_phone',
                title: 'Phone Number'
            }
        ],
    
        add_legal_location: [
            {
                key: 'address',
                title: 'Address'
            }
        ],
        edit_legal_location: [
            {
                key: 'address',
                title: 'Address'
            }
        ],
        remove_legal_location: [
            {
                key: 'address',
                title: 'Address'
            }
        ],

        update_supplier_status: [
            {
                key: 'old_status',
                title: 'Previous Status',
                valueRenderer: ({ value, t }) => {
                    return t(SupplierStatuses[value]?.title)
                }
            },
            {
                key: 'status',
                title: 'New Status',
                valueRenderer: ({ value, t }) => {
                    return t(SupplierStatuses[value]?.title)
                }
            }
        ],
        edit_supplier_analyst_admin: [
            {
                key: 'analyst_admin',
                title: 'Assigned Analyst'
            }
        ],
        supplier_toggle_flag: [
            {
                key: 'flag',
                title: 'Flag Status',
                valueRenderer: ({ value, t }) => {
                    return t(value ? 'True' : 'False')
                }
            }
        ],
        supplier_finding_toggle_flag: [
            {
                key: 'flag',
                title: 'Flag Status',
                valueRenderer: ({ value, t }) => {
                    return t(value ? 'True' : 'False')
                }
            }
        ],

        create_report_request: [
            {
                key: 'report_request_id',
                title: 'Report ID'
            },
            {
                key: 'responsible_person_name',
                title: 'Responsible Person'
            },
            {
                key: 'deadline_date',
                title: 'Deadline'
            },
            {
                key: 'questionnaire_name',
                title: 'Report Type'
            }
        ],
        withdraw_report_request: [
            {
                key: 'report_request_id',
                title: 'Report ID'
            },
            {
                key: 'responsible_person_name',
                title: 'Responsible Person'
            },
            {
                key: 'deadline_date',
                title: 'Deadline'
            },
            {
                key: 'questionnaire_name',
                title: 'Report Type'
            }
        ],

        supplier_finding_toggle_read_flag: [
            {
                key: 'flag',
                title: 'New Status',
                valueRenderer: ({ value, t }) => {
                    return t(value ? 'Read' : 'Unread')
                }
            }
        ]
    },
    questionnaireDesigner: {
        create_questionnaire: [
            {
                key: "questionnaire_secondary_id",
                title: "Questionnaire ID"
            },
            {
                key: "questionnaire_name",
                title: "Questionnaire Title",
                valueRenderer: ({ value, t }) => {     
                    return t(value)
                }
            }
        ],  
        clone_questionnaire: [
            {
                key: "questionnaire_secondary_id",
                title: "Questionnaire ID"
            },
            {
                key: "questionnaire_name",
                title: "Questionnaire Title",
                valueRenderer: ({ value, t }) => {     
                    return t(value)
                }
            },
            {
                key: "questionnaire_clone_from",
                title: "Clone From",
                valueRenderer: ({ value, t }) => {     
                    return t(value)
                }
            }
        ],
        update_questionnaire: [],
        active_questionnaire: [
            {
                key: "questionnaire_secondary_id",
                title: "Questionnaire ID"
            },
            {
                key: "questionnaire_name",
                title: "Questionnaire Title",
                valueRenderer: ({ value, t }) => {     
                    return t(value)
                }
            }
        ],
        in_active_questionnaire: [
            {
                key: "questionnaire_secondary_id",
                title: "Questionnaire ID"
            },
            {
                key: "questionnaire_name",
                title: "Questionnaire Title",
                valueRenderer: ({ value, t }) => {     
                    return t(value)
                }
            }
        ],
        delete_questionnaire: [
            {
                key: "questionnaire_secondary_id",
                title: "Questionnaire ID"
            },
            {
                key: "questionnaire_name",
                title: "Questionnaire Title",
                valueRenderer: ({ value, t }) => {     
                    return t(value)
                }
            }
        ]
    },
    kyb: {
        delete_compliance_screening: [
            {
                key: "search_query",
                title: "Search Query (e.g., name)"
            },
            {
                key: "risk_rating",
                title: "Risk Rating",
                valueRenderer: ({ value, t }) => {
                    let color = 'bg-success';

                    if (value === 'medium') {
                        color = 'bg-warning';
                    }
                    else if (value === 'high') {
                        color = 'bg-danger';
                    }

                    return (
                        <div
                            className="d-flex gap-1"
                        >
                            <span
                                class={`dt-risk-rating-bullet ${color}`}
                            />
                            {t(value)}
                        </div>
                    )
                }
            },
            {
                key: "executed_at",
                title: "Executed At",
                valueRenderer: ({ value }) => {
                    return dateUtils.convertDateToDate(
                        value,
                        INTERNATIONAL_DATE_FORMAT
                    )
                }
            }
        ],
        search_persons: [
            {
                key: "member_names",
                title: "Members",
                valueRenderer: ({ value }) => {
                    return (
                        <div className="d-flex justify-content-center align-items-start flex-column">
                            {(value || []).map((memberName) => {
                                return (
                                    <p className="mb-0 d-flex justify-content-center align-items-center">
                                        <i className="ri-check-line me-1" />
                                        {memberName}
                                    </p>
                                )
                            })}
                        </div>
                    )
                }
            }
        ],
        create_compliance_screening: [
            {
                key: "search_query",
                title: "Search Query (e.g., name)"
            },
            {
                key: "person_id",
                title: "Person ID"
            },
            {
                key: "risk_rating",
                title: "Risk Rating",
                valueRenderer: ({ value, t }) => {
                    let color = 'bg-success';

                    if (value === 'medium') {
                        color = 'bg-warning';
                    }
                    else if (value === 'high') {
                        color = 'bg-danger';
                    }

                    return (
                        <div
                            className="d-flex gap-1"
                        >
                            <span
                                class={`dt-risk-rating-bullet ${color}`}
                            />
                            {t(value)}
                        </div>
                    )
                }
            }
        ],
        fetch_from_source: [
            {
                key: "provider",
                title: "Provider"
            },
            {
                key: "company_source_id",
                title: "Source ID"
            }
        ],
        search_companies: [
            {
                key: "provider",
                title: "Provider"
            },
            {
                key: "companies_count",
                title: "Third Parties Count"
            }
        ]
    }, 
    kybMonitoring: {
        fetch_alerts_and_reviews: [
            {
                key: "pageSize",
                title: "Page Size",
                hideIfValueIsUndefined: true
            },
            {
                key: "pageIndex",
                title: "Page Index",
                hideIfValueIsUndefined: true
            },
            {
                key: "itemsCount",
                title: "Items Count",
                hideIfValueIsUndefined: true
            },
        ],
        create_monitoring_query: [
            {
                key: "query_id",
                title: "Query ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, history, t }) => {
                    return (
                        <div>
                            <span className="me-1">{value}</span>
                            <Button
                                color="primary"
                                outline
                                id="view-query-button"
                                className="border-0"
                                onClick={() => {
                                    const detailsPath = history.createHref({
                                        pathname: `/admin/3rd-party/compliance/queries/${value}/details`,
                                        search: "?type=screening"
                                    });
                                    const detailsUrl = new URL(detailsPath, window.location.origin).toString();
                                    window.open(detailsUrl, "_blank", "noopener,noreferrer");
                                }}
                            >
                                <i className="ri-eye-line"></i>
                                <UncontrolledTooltip
                                    placement="top"
                                    target="view-query-button"
                                >
                                    {t('View Query')}
                                </UncontrolledTooltip>
                            </Button>
                        </div>
                    )
                }
            },
            {
                key: "query_name",
                title: "Query Name",
                hideIfValueIsUndefined: true
            },
            {
                key: "items_count",
                title: "Search Items Count",
                hideIfValueIsUndefined: true
            }
        ],
        finalize_monitoring_query: [
            {
                key: "query_id",
                title: "Query ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, history, t }) => {
                    return (
                        <div>
                            <span className="me-1">{value}</span>
                            <Button
                                color="primary"
                                outline
                                id="view-query-button"
                                className="border-0"
                                onClick={() => {
                                    const detailsPath = history.createHref({
                                        pathname: `/admin/3rd-party/compliance/queries/${value}/details`,
                                        search: "?type=screening"
                                    });
                                    const detailsUrl = new URL(detailsPath, window.location.origin).toString();
                                    window.open(detailsUrl, "_blank", "noopener,noreferrer");
                                }}
                            >
                                <i className="ri-eye-line"></i>
                                <UncontrolledTooltip
                                    placement="top"
                                    target="view-query-button"
                                >
                                    {t('View Query')}
                                </UncontrolledTooltip>
                            </Button>
                        </div>
                    )
                }
            },
            {
                key: "query_name",
                title: "Query Name",
                hideIfValueIsUndefined: true
            },
            {
                key: "items_count",
                title: "Search Items Count",
                hideIfValueIsUndefined: true
            }
        ],
        delete_monitoring_query: [
            {
                key: "query_id",
                title: "Query ID",
                hideIfValueIsUndefined: true
            },
            {
                key: "query_name",
                title: "Query Name",
                hideIfValueIsUndefined: true
            },
            {
                key: "deleted_items_count",
                title: "Search Items Count",
                hideIfValueIsUndefined: true
            }
        ]
    },
    kybScreening: {
        create_screening_query: [
            {
                key: "query_id",
                title: "Query ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, history, t }) => {
                    return (
                        <div>
                            <span className="me-1">{value}</span>
                            <Button
                                color="primary"
                                outline
                                id="view-query-button"
                                className="border-0"
                                onClick={() => {
                                    const detailsPath = history.createHref({
                                        pathname: `/admin/3rd-party/compliance/queries/${value}/details`,
                                        search: "?type=screening"
                                    });
                                    const detailsUrl = new URL(detailsPath, window.location.origin).toString();
                                    window.open(detailsUrl, "_blank", "noopener,noreferrer");
                                }}
                            >
                                <i className="ri-eye-line"></i>
                                <UncontrolledTooltip
                                    placement="top"
                                    target="view-query-button"
                                >
                                    {t('View Query')}
                                </UncontrolledTooltip>
                            </Button>
                        </div>
                    )
                }
            },
            {
                key: "query_name",
                title: "Query Name",
                hideIfValueIsUndefined: true
            },
            {
                key: "items_count",
                title: "Search Items Count",
                hideIfValueIsUndefined: true
            }
        ],
        finalize_screening_query: [
            {
                key: "query_id",
                title: "Query ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, history, t }) => {
                    return (
                        <div>
                            <span className="me-1">{value}</span>
                            <Button
                                color="primary"
                                outline
                                id="view-query-button"
                                className="border-0"
                                onClick={() => {
                                    const detailsPath = history.createHref({
                                        pathname: `/admin/3rd-party/compliance/queries/${value}/details`,
                                        search: "?type=screening"
                                    });
                                    const detailsUrl = new URL(detailsPath, window.location.origin).toString();
                                    window.open(detailsUrl, "_blank", "noopener,noreferrer");
                                }}
                            >
                                <i className="ri-eye-line"></i>
                                <UncontrolledTooltip
                                    placement="top"
                                    target="view-query-button"
                                >
                                    {t('View Query')}
                                </UncontrolledTooltip>
                            </Button>
                        </div>
                    )
                }
            },
            {
                key: "query_name",
                title: "Query Name",
                hideIfValueIsUndefined: true
            },
            {
                key: "items_count",
                title: "Search Items Count",
                hideIfValueIsUndefined: true
            }
        ],
        delete_screening_query: [
            {
                key: "query_id",
                title: "Query ID",
                hideIfValueIsUndefined: true
            },
            {
                key: "query_name",
                title: "Query Name",
                hideIfValueIsUndefined: true
            },
            {
                key: "deleted_items_count",
                title: "Search Items Count",
                hideIfValueIsUndefined: true
            }
        ]
    },
    task: {
        create: [
            {
                key: "secondary_task_id",
                title: "Task ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, otherFields, logData }) => {
                    return taskIdGenerator({
                        task: {
                            secondary_id: value,
                            relatedRisk: otherFields?.relatedRisk,
                            organization: otherFields?.organization
                                ? {
                                    ...otherFields.organization,
                                    isOwnCompany: otherFields.organization.is_own_company, 
                                    secondaryId: otherFields.organization.secondary_id
                                }
                                : {
                                    isOwnCompany: logData?.supplier?.isOwnCompany,
                                    secondaryId: logData?.supplier?.id
                                },
                            supplier: logData?.supplier?.id
                        }
                    });
                }
            }
        ],
        
        assign_analyst: [
            {
                key: "secondary_task_id",
                title: "Task ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, otherFields, logData }) => {
                    return taskIdGenerator({
                        task: {
                            secondary_id: value,
                            relatedRisk: otherFields?.relatedRisk,
                            organization: otherFields?.organization
                                ? {
                                    ...otherFields.organization,
                                    secondaryId: otherFields.organization.secondary_id
                                }
                                : {
                                    isOwnCompany: logData?.supplier?.isOwnCompany,
                                    secondaryId: logData?.supplier?.id
                                },
                            supplier: logData?.supplier?.id
                        }
                    });
                }
            },
            {
                key: "analyst_name",
                title: "Assigned Analyst",
                hideIfValueIsUndefined: true
            }       

        ],
        
        update_deadline: [
            {
                key: "secondary_task_id",
                title: "Task ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, otherFields, logData }) => {
                    return taskIdGenerator({
                        task: {
                            secondary_id: value,
                            relatedRisk: otherFields?.relatedRisk,
                            organization: otherFields?.organization
                                ? {
                                    ...otherFields.organization,
                                    secondaryId: otherFields.organization.secondary_id
                                }
                                : {
                                    isOwnCompany: logData?.supplier?.isOwnCompany,
                                    secondaryId: logData?.supplier?.id
                                },
                            supplier: logData?.supplier?.id
                        }
                    });
                }
            },
            {
                key: "deadline",
                title: "Deadline",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value }) => {
                    return dateUtils.convertDateToDate(
                        value,
                        INTERNATIONAL_DATE_FORMAT
                    );
                }
            }
        ],
        
        update_status: [
            {
                key: "secondary_task_id",
                title: "Task ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, otherFields, logData }) => {
                    return taskIdGenerator({
                        task: {
                            secondary_id: value,
                            relatedRisk: otherFields?.relatedRisk,
                            organization: otherFields?.organization
                                ? {
                                    ...otherFields.organization,
                                    secondaryId: otherFields.organization.secondary_id
                                }
                                : {
                                    isOwnCompany: logData?.supplier?.isOwnCompany,
                                    secondaryId: logData?.supplier?.id
                                },
                            supplier: logData?.supplier?.id
                        }
                    });
                }
            },
            {
                key: "status",
                title: "Status",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, t }) => {
                    return t(TasksStatuses[value]?.title || value);
                }
            }
        ],
        
        update_priority: [
            {
                key: "secondary_task_id",
                title: "Task ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, otherFields, logData }) => {
                    return taskIdGenerator({
                        task: {
                            secondary_id: value,
                            relatedRisk: otherFields?.relatedRisk,
                            organization: otherFields?.organization
                                ? {
                                    ...otherFields.organization,
                                    secondaryId: otherFields.organization.secondary_id
                                }
                                : {
                                    isOwnCompany: logData?.supplier?.isOwnCompany,
                                    secondaryId: logData?.supplier?.id
                                },
                            supplier: logData?.supplier?.id
                        }
                    });
                }
            },
            {
                key: "status",
                title: "Status",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, t }) => {
                    return t(TasksPriorities[value]?.title || value);
                }
            }
        ],
    
        update_category: [
            {
                key: "secondary_task_id",
                title: "Task ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, otherFields, logData }) => {
                    return taskIdGenerator({
                        task: {
                            secondary_id: value,
                            relatedRisk: otherFields?.relatedRisk,
                            organization: otherFields?.organization
                                ? {
                                    ...otherFields.organization,
                                    secondaryId: otherFields.organization.secondary_id
                                }
                                : {
                                    isOwnCompany: logData?.supplier?.isOwnCompany,
                                    secondaryId: logData?.supplier?.id
                                },
                            supplier: logData?.supplier?.id
                        }
                    });
                }
            },
            {
                key: "category",
                title: "Category",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, t }) => {
                    return t(value);
                }
            }
        ],
    
        delete: [
            {
                key: "secondary_task_id",
                title: "Task ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, otherFields, logData }) => {
                    return taskIdGenerator({
                        task: {
                            secondary_id: value,
                            relatedRisk: otherFields?.relatedRisk,
                            organization: otherFields?.organization
                                ? {
                                    ...otherFields.organization,
                                    secondaryId: otherFields.organization.secondary_id
                                }
                                : {
                                    isOwnCompany: logData?.supplier?.isOwnCompany,
                                    secondaryId: logData?.supplier?.id
                                },
                            supplier: logData?.supplier?.id
                        }
                    });
                }
            }
        ],

        create_comment: [
            {
                key: "secondary_task_id",
                title: "Task ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, otherFields, logData }) => {
                    return taskIdGenerator({
                        task: {
                            secondary_id: value,
                            relatedRisk: otherFields?.relatedRisk,
                            organization: otherFields?.organization
                                ? {
                                    ...otherFields.organization,
                                    secondaryId: otherFields.organization.secondary_id
                                }
                                : {
                                    isOwnCompany: logData?.supplier?.isOwnCompany,
                                    secondaryId: logData?.supplier?.id
                                },
                            supplier: logData?.supplier?.id
                        }
                    });
                }
            },
            {
                key: "comment",
                title: "Comment",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value }) => {
                    if (!value) return "-";
        
                    return value.replace(/<[^>]*>/g, "").trim() || "-";
                }
            },
            {
                key: "attachments",
                title: "Attachments",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, t }) => {
                    if (!Array.isArray(value) || value.length === 0) {
                        return t("No attachments");
                    }
        
                    return (
                        <div className="d-flex justify-content-center align-items-start flex-column">
                            {value.map((item) => (
                                <p key={item?.id} className="mb-0 d-flex justify-content-center align-items-center">
                                    <i className="ri-attachment-2 me-1" />
                                    {item?.name}
                                </p>
                            ))}
                        </div>
                    );
                }
            }
        ],
        download_file: [
            {
                key: "secondary_task_id",
                title: "Task ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, otherFields, logData }) => {
                    return taskIdGenerator({
                        task: {
                            secondary_id: value,
                            relatedRisk: otherFields?.relatedRisk,
                            organization: otherFields?.organization
                                ? {
                                    ...otherFields.organization,
                                    secondaryId: otherFields.organization.secondary_id
                                }
                                : {
                                    isOwnCompany: logData?.supplier?.isOwnCompany,
                                    secondaryId: logData?.supplier?.id
                                },
                            supplier: logData?.supplier?.id
                        }
                    });
                }
            },
            {
                key: "file_name",
                title: "File",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value }) => {
                    return value || "-";
                }
            }
        ]
    },
    risk: {
        create: [
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, otherFields, logData }) => {
                    return riskIdGenerator({
                        risk: {
                            secondaryId: value,
                            supplierId: logData?.supplier?.id,
                            organization: otherFields?.organization
                                ? {
                                    ...otherFields.organization,
                                    secondaryId: otherFields.organization.secondary_id
                                }
                                : {
                                    isOwnCompany: logData?.supplier?.isOwnCompany,
                                    secondaryId: logData?.supplier?.id
                                }
                        }
                    });
                }
            },
        ],
        update_category: [
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, otherFields, logData }) => {
                    return riskIdGenerator({
                        risk: {
                            secondaryId: value,
                            supplierId: logData?.supplier?.id,
                            organization: otherFields?.organization
                                ? {
                                    ...otherFields.organization,
                                    secondaryId: otherFields.organization.secondary_id
                                }
                                : {
                                    isOwnCompany: logData?.supplier?.isOwnCompany,
                                    secondaryId: logData?.supplier?.id
                                }
                        }
                    });
                }
            },
            {
                key: "category",
                title: "Category",
                hideIfValueIsUndefined: true
            },
            {
                key: "sub_category",
                title: "Sub Category",
                hideIfValueIsUndefined: true
            }
        ],
        update_likelihood: [
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, otherFields, logData }) => {
                    return riskIdGenerator({
                        risk: {
                            secondaryId: value,
                            supplierId: logData?.supplier?.id,
                            organization: otherFields?.organization
                                ? {
                                    ...otherFields.organization,
                                    secondaryId: otherFields.organization.secondary_id
                                }
                                : {
                                    isOwnCompany: logData?.supplier?.isOwnCompany,
                                    secondaryId: logData?.supplier?.id
                                }
                        }
                    });
                }
            },
            {
                key: "likelihood",
                title: "Likelihood",
                hideIfValueIsUndefined: true
            },
            {
                key: "new_risk",
                title: "Risk",
                hideIfValueIsUndefined: true
            }
        ],
        update_damage: [
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, otherFields, logData }) => {
                    return riskIdGenerator({
                        risk: {
                            secondaryId: value,
                            supplierId: logData?.supplier?.id,
                            organization: otherFields?.organization
                                ? {
                                    ...otherFields.organization,
                                    secondaryId: otherFields.organization.secondary_id
                                }
                                : {
                                    isOwnCompany: logData?.supplier?.isOwnCompany,
                                    secondaryId: logData?.supplier?.id
                                }
                        }
                    });
                }
            },
            {
                key: "damage",
                title: "Damage",
                hideIfValueIsUndefined: true
            },
            {
                key: "new_risk",
                title: "Risk Level",
                hideIfValueIsUndefined: true
            }
        ],
        create_comment: [
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, otherFields, logData }) => {
                    return riskIdGenerator({
                        risk: {
                            secondaryId: value,
                            supplierId: logData?.supplier?.id,
                            organization: otherFields?.organization
                                ? {
                                    ...otherFields.organization,
                                    secondaryId: otherFields.organization.secondary_id
                                }
                                : {
                                    isOwnCompany: logData?.supplier?.isOwnCompany,
                                    secondaryId: logData?.supplier?.id
                                }
                        }
                    });
                }
            },
            {
                key: "comment",
                title: "Comment",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value }) => {
                    if (!value) return "-";
        
                    return value.replace(/<[^>]*>/g, "").trim() || "-";
                }
            },
            {
                key: "attachments",
                title: "Attachments",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, t }) => {
                    if (!Array.isArray(value) || value.length === 0) {
                        return t("No attachments");
                    }
        
                    return (
                        <div className="d-flex justify-content-center align-items-start flex-column">
                            {value.map((item) => (
                                <p key={item?.id} className="mb-0 d-flex justify-content-center align-items-center">
                                    <i className="ri-attachment-2 me-1" />
                                    {item?.name}
                                </p>
                            ))}
                        </div>
                    );
                }
            }
        ],        
        delete: [
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, otherFields, logData }) => {
                    return riskIdGenerator({
                        risk: {
                            secondaryId: value,
                            supplierId: logData?.supplier?.id,
                            organization: otherFields?.organization
                                ? {
                                    ...otherFields.organization,
                                    secondaryId: otherFields.organization.secondary_id
                                }
                                : {
                                    isOwnCompany: logData?.supplier?.isOwnCompany,
                                    secondaryId: logData?.supplier?.id
                                }
                        }
                    });
                }
            },
        ],
        update_description: [
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, otherFields, logData }) => {
                    return riskIdGenerator({
                        risk: {
                            secondaryId: value,
                            supplierId: logData?.supplier?.id,
                            organization: otherFields?.organization
                                ? {
                                    ...otherFields.organization,
                                    secondaryId: otherFields.organization.secondary_id
                                }
                                : {
                                    isOwnCompany: logData?.supplier?.isOwnCompany,
                                    secondaryId: logData?.supplier?.id
                                }
                        }
                    });
                }
            },
            {
                key: "description",
                title: "Description",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value }) => {
                    if (!value) return "-";
        
                    return value.replace(/<[^>]*>/g, "").trim() || "-";
                }
            }
        ],
        assign_analyst: [
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, otherFields, logData }) => {
                    return riskIdGenerator({
                        risk: {
                            secondaryId: value,
                            supplierId: logData?.supplier?.id,
                            organization: otherFields?.organization
                                ? {
                                    ...otherFields.organization,
                                    secondaryId: otherFields.organization.secondary_id
                                }
                                : {
                                    isOwnCompany: logData?.supplier?.isOwnCompany,
                                    secondaryId: logData?.supplier?.id
                                }
                        }
                    });
                }
            },
            {
                key: "analyst_name",
                title: "Assigned Analyst",
                hideIfValueIsUndefined: true
            }       
        ],
        download_file: [
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, otherFields, logData }) => {
                    return riskIdGenerator({
                        risk: {
                            secondaryId: value,
                            supplierId: logData?.supplier?.id,
                            organization: otherFields?.organization
                                ? {
                                    ...otherFields.organization,
                                    secondaryId: otherFields.organization.secondary_id
                                }
                                : {
                                    isOwnCompany: logData?.supplier?.isOwnCompany,
                                    secondaryId: logData?.supplier?.id
                                }
                        }
                    });
                }
            },
            {
                key: "file_name",
                title: "File",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value }) => {
                    return value || "-";
                }
            }
        ]
    }
}

export const HistoryLogActions = {
	report      :   {   
        create                      :   'New report created',
		update_analyst              :   'Update Analyst',
		update_status               :   'Update Status',
		mark_as_delete              :   'Marked as delete',
		delete                      :   'Deleted',
		upload_attachment           :   'Uploaded attachment',
		download_user_attachment    :   'Download user attachment',
		download_analyst_attachment :   'Download analyst attachment',
		delete_attachment           :   'Delete Attachment',
		add_comment                 :   'Add Comment',
		add_internal_comment        :   'Add Internal Comment',
		update_analyst_readonly     :   'Read-Only Analysts',
		update_risk                 :   'Risk updated',
		link_case                   :   'Linked case updated',
		change_admin_description    :   'Admin description updated',
		change_admin_category       :   'Admin category updated',
		add_link_case               :   "Linked case added",
		remove_link_case            :   "Linked case removed",
	},
	risk        :   {
		upload_files        :   'Uploaded files',
		upload_file         :   'Uploaded file',
		download_files      :   'Download files',
		download_file       :   'Download file',
		update_file         :   'Upload file',
		delete_file         :   'Delete file',
		create_comment      :   'New comment created',
		create              :   'New risk created',
		update_description  :   'Description updated',
		delete              :   'Deleted',
		assign_analyst      :   'Assigned to analyst',
		update_damage       :   'Damage updated',
		update_likelihood   :   'Likelihood updated',
		update_category     :   'Category updated',
		change_description  :   'Description updated by admin',
		change_category     :   'Category updated by admin',
		create_ai           :   'New Ai risk created',
	},
	task        :   {
		upload_files        :   'Uploaded files',
		upload_file         :   'Uploaded file',
		download_files      :   'Download files',
		download_file       :   'Download file',
		update_file         :   'Upload file',
		delete_file         :   'Delete file',
		create_comment      :   'New comment created',
		create              :   'New task created',
		assign_analyst      :   'Assigned to analyst',
		update_description  :   'Description updated',
		update_status       :   'Status updated',
		update_priority     :   'Priority updated',
		update_deadline     :   'Deadline updated',
		update_category     :   'Category updated',
		delete              :   'Deleted',
	},
	admin       :   {
		change_active_directory_settings    :   'Active directory settings',
		edit_risk_matrix                    :   'Update matrix',
		restore_risk_matrix                 :   'Restore matrix',
		edit_user                           :   'Edit user',
		create_user                         :   'Created user',
		delete_user                         :   'Deleted user',
		change_user_status                  :   'Changed user status',
		edit_organization                   :   'Edited organization',
		update_question                     :   'Updated question',
		remove_question                     :   'Removed question',
		remove_category                     :   'Removed category',
		login                               :   'Login',
        add_tag                             :   "Add keyword",
        remove_tag                          :   "Remove keyword",
        edit_mailer_setting                 :   "Updated mail settings",
        toggle_mailer_setting_active        :   "Toggled mail settings active status"
	},
	document    :   { 
        create  :   'Created', 
        remove  :   'Removed' 
    },
	user        :   { 
        approveConsent  : "Approve Consent"
    },
    supplier    :   {
        'onboard'               :   'New supplier created',
        'first_assessment'      :   'First assessment based on findings',
        'new_report'            :   'New report created',
        'add_comment'           :   'Add Comment',
        'add_internal_comment'  :   'Add Internal Comment',

        'add_team_manager': 'Add Management Team Person',
        'edit_team_manager': 'Edit Management Team Person',
        'delete_team_manager': 'Delete Management Team Person',

        'add_connected_legal': 'Add Connected Legal',
        'delete_connected_legal': 'Delete Connected Legal',
        'update_connected_legal': 'Edit Connected Legal',

        'add_share_holder': 'Add Shareholder',
        'edit_share_holder': 'Edit Shareholder',
        'delete_share_holder': 'Delete Shareholder',

        'add_product': 'Add Product',
        'edit_product': 'Edit Product',
        'delete_product': 'Delete Product',

        'create_supplier_location': 'Add Location',
        'edit_supplier_location': 'Edit Location',
        'delete_supplier_location': 'Delete Location',

        'add_contact_person': 'Add Contact Person',
        'edit_contact_person': 'Edit Contact Person',
        'delete_contact_person': 'Delete Contact Person',

        'update_supplier_status': 'Update Third Party Status',
        'edit_supplier_analyst_admin': 'Update Assigned Analyst',
        'edit_supplier_general_information': 'Edit General Information',
        'supplier_toggle_flag': 'Toggle Flag',
        'supplier_finding_toggle_flag': 'Toggle Finding Flag',

        'create_report_request': 'Create Report Request',
        'withdraw_report_request': 'Report Request Withdrawn',
        
        'supplier_finding_toggle_read_flag': 'Toggle Finding Read Status',

        'save_geolocation': 'Geolocation added/updated',
        'update_dds_declaration': 'DDS declaration updated',

        'edit_supplier_information': 'Supplier information updated',
        'onboard_company': 'New company created',
    },
    ownCompany     :   {
        'onboard_company'       :   'New company created',
        'first_assessment'      :   'First assessment based on findings',
        'new_report'            :   'New report created',
        'add_comment'           :   'Add Comment',
        'add_internal_comment'  :   'Add Internal Comment',

        'add_team_manager': 'Add Management Team Person',
        'edit_team_manager': 'Edit Management Team Person',
        'delete_team_manager': 'Delete Management Team Person',

        'add_connected_legal': 'Add Connected Legal',
        'delete_connected_legal': 'Delete Connected Legal',
        'update_connected_legal': 'Edit Connected Legal',

        'add_share_holder': 'Add Shareholder',
        'edit_share_holder': 'Edit Shareholder',
        'delete_share_holder': 'Delete Shareholder',

        'add_product': 'Add Product',
        'edit_product': 'Edit Product',
        'delete_product': 'Delete Product',

        'create_supplier_location': 'Add Location',
        'edit_supplier_location': 'Edit Location',
        'delete_supplier_location': 'Delete Location',

        'add_contact_person': 'Add Contact Person',
        'edit_contact_person': 'Edit Contact Person',
        'delete_contact_person': 'Delete Contact Person',

        'update_supplier_status': 'Update Third Party Status',
        'edit_supplier_analyst_admin': 'Update Assigned Analyst',
        'edit_supplier_general_information': 'Edit General Information',
        'supplier_toggle_flag': 'Toggle Flag',
        'supplier_finding_toggle_flag': 'Toggle Finding Flag',

        'create_report_request': 'Create Report Request',
        'withdraw_report_request': 'Report Request Withdrawn',

        'supplier_finding_toggle_read_flag': 'Toggle Finding Read Status'
    },
    entityManagement : {
        add_institute  :  'Add Institute',
        edit_institute  :  'Edit Institute',
        remove_institute : 'Remove Institute',
        add_division: 'Add Department',
        edit_division: 'Edit Department',
        remove_division: 'Remove Department',
        add_legal_entity : 'Add Legal Entity',
        edit_legal_entity: 'Edit Legal Entity',
        remove_legal_entity: 'Remove Legal Entity',
        delete: 'Delete Legal Entity',
        force_delete: 'Force Delete Legal Entity',
        add_legal_location: 'Add Legal Location',
        edit_legal_location: 'Edit Legal Location',
        remove_legal_location: 'Remove Legal Location',
        edit_routing: 'Edit Routing Settings',
        edit_financial_setting: 'Edit Financial Year Settings'
    },
    questionnaireDesigner: {
        create_questionnaire: 'Create Questionnaire',
        clone_questionnaire: 'Clone Questionnaire',
        update_questionnaire: 'Edit Questionnaire',
        active_questionnaire: 'Activate Questionnaire',
        in_active_questionnaire: 'Deactivate Questionnaire',
        delete_questionnaire: 'Delete Questionnaire'
    },
    kyb: {
        search_persons: 'Performed Individual Screening Search',
        create_compliance_screening: 'Fetched Screening Details for Individual',
        search_companies: 'Performed Third Party Screening Search',
        fetch_from_source: 'Fetched Screening Details for Third Party',
        delete_compliance_screening: 'Deleted Screening',
    },
    kybMonitoring: {
        add_suppliers_to_monitoring: 'Added Third Parties to Monitoring',
        fetch_alerts_and_reviews: 'Fetched Alerts and Reviews',
        get_monitoring_result_detail: 'Viewed Monitoring Result Details',
        get_monitoring_alert_detail: 'Viewed Alert Details',
        mark_alert_false_positive: 'Marked Alert as False Positive',
        add_person_to_follow_list: 'Added Person to Follow List',
        delete_whole_follow_list: 'Deleted Follow List',
        delete_follow_list_items: 'Removed Items from Follow List',
        create_monitoring_query: 'Created Monitoring Query',
        finalize_monitoring_query: 'Finalized Monitoring Query',
        update_monitoring_query: 'Updated Monitoring Query',
        delete_monitoring_query: 'Deleted Monitoring Query'
    },
    kybScreening: {
        create_screening_query: 'Created Screening Query',
        finalize_screening_query: 'Finalized Screening Query',
        update_screening_query: 'Updated Screening Query',
        delete_screening_query: 'Deleted Screening Query'
    }
};

export const HistoryLogSections = {
	risk        :   'Risk Management',
	task        :   'Task Management',
	admin       :   'Administration',
	report      :   'Report',
	document    :   'Document Management',
	user        :   "User",
    supplier    :   'Supplier',
    ownCompany  :   'Company',
    entityManagement: 'Entity Management',
    connectedLegal: 'Connected Legal Entities',
    shareHolder: 'Shareholders',
    product: 'Products',
    questionnaireDesigner: 'Questionnaire Designer',
    kyb: 'KYB',
    kybMonitoring: 'KYB Monitoring',
    kybScreening: 'KYB Screening'
};

export const HistoryLogRoles = {
	ROLE_ANALYST_ADMIN  :   'Analyst admin',
	ROLE_ANALYST        :   'Analyst',
	ROLE_TASK_OWNER     :   'Task Owner',
	ROLE_THIRD_PARTY    :   'Third Party',
	ROLE_EMPLOYEE       :   'Employee'
};

export const TasksStatuses = {
	NEW				:	{
		title	:	'New',
		color	:	'#FFEE93'
	},
	IN_PROGRESS		:	{
		title	:	'In Progress',
		color	:	'#8792E8'
	},
	REVIEW			:	{
		title	:	'Review',
		color	:	'#85FF8A'
	},
	CLOSED			:	{
		title	:	'Closed',
		color	:	'#EDECEC'
	},
	DELETED			:	{
		title	:	'Deleted',
		color	:	'#FFDADA'
	}
}

export const TasksPriorities = {
	low		:	{
		title	:	"Low",
		color	:	"#1CBB8C",
		icon	:	<HiChevronDoubleDown />
	},
	medium	:	{
		title	:	"Medium",
		color	:	"#FCC34B",
		icon	:	<HiMenuAlt4 />
	},
	high	:	{
		title	:	"High",
		color	:	"#DC3545",
		icon	:	<HiChevronDoubleUp />
	}
};

export const SuppliersStatuses = {					
	0	:	{
		title	:	'New',
		color	:	'#FFEE93'
	},
	1	:	{
		title	:	'Under Review',
		color	:	'#74B8FF'
	},
	2	:	{
		title	:	'Approved',
		color	:	'#4CD4AC'
	},
	3	:	{
		title	:	'Rejected',
		color	:	'#BE4F59'
	}
}

export const CompanySizes = {
	"0-10"	:	{
		title	:	"0-10"
	},
	"10-50"	:	{
		title	:	"10-50"
	},
	"50-100"	:	{
		title	:	"50-100"
	},
	"100-500"	:	{
		title	:	"100-500"
	},
	"500-1000"	:	{
		title	:	"500-1000"
	},
	"1000-2000"	:	{
		title	:	"1000-2000"
	},
	"2000-5000"	:	{
		title	:	"2000-5000"
	},
	"+5000"		:	{
		title	:	"+5000"
	}
};

export const RisksRates = {
    "low": {
        title: "Low",
        color: "#1CBB8C"
    },
    "medium": {
        title: "Medium",
        color: "#FCC34B"
    },
    "high": {
        title: "High",
        color: "#DC3545"
    },

    
    "insignificant": {
        title: "Insignificant",
        color: "#4EB8B1"
    },
    "minor": {
        title: "Minor",
        color: "#59C94F"
    },
    "noticeable": {
        title: "Noticeable",
        color: "#FFA049"
    },
    "critical": {
        title: "Critical",
        color: "#FF4949"
    },
    "existence - threatening": {
        title: "Existence - threatening",
        color: "#822626"
    },
}

export const customFonts = [
    {
      style: "normal",
      family: "Inter",
      weight: 400,
    },
    {
      style: "normal",
      family: "Nunito",
      weight: 400,
    },
    {
      style: "normal",
      family: "Raleway",
      weight: 400,
    },
    {
      style: "normal",
      family: "Roboto Mono",
      weight: 400,
    },
    {
      style: "normal",
      family: "Noto Serif Toto",
      weight: 400,
    },
    {
      style: "normal",
      family: "Kumbh Sans",
      weight: 400,
    },
    {
      style: "normal",
      family: "Padyakke Expanded One",
      weight: 400,
    },
    {
      style: "normal",
      family: "Noto Sans Cherokee",
      weight: 400,
    },
  ];

export const DocumentsTypes = [
    {
        typeKey: 'regular',
        title: 'Other'
    },
    {
        typeKey: 'user_policy',
        title: 'User policy'
    },
    {
        typeKey: 'privacy_policy',
        title: 'Privacy policy'
    }
];

export const SuppliersScorings = {
    "highRisk": {
        title: "High Risk",
        color: "#822626"
    },
    "moderate": {
        title: "Moderate Risk",
        color: "#FF4949"
    },
    "average": {
        title: "Average",
        color: "#FFA049"
    },
    "lowRisk": {
        title: "Low Risk",
        color: "#59C94F"
    },
    "excellent": {
        title: "Excellent",
        color: "#4EB8B1"
    }
}

export const SupplierLatestReportsStatuses = {
    "submitted"   :   {
        title       :   "Submitted",
        color       :   "#d4edda",
        fontColor   :   "#155724" 
    },
    "not_requested"    :   {
        title       :   "Not Requested",
        color       :   "#f5f5f5",
        fontColor   :   "#6c757d"
    },
    "requested"    :   {
        title       :   "Requested",
        color       :   "#d9f7ff",
        fontColor   :   "#6c757d"
    },
    "waiting"  :   {
        title       :   "Waiting",
        color       :   "#fff9db",
        fontColor   :   "#856404"
    },
    "expired"  :   {
        title       :   "Expired",
        color       :   "#f8d7da",
        fontColor   :   "#721c24"
    },
    "withdrawn": {
        title       :   "Withdrawn",
        color       :   "#FFA500",
        fontColor   :   "#155724"
    }
}

export const QuestionnaireStatuses = {
    'active' : {
        title: 'Active',
        color: "#4CD4AC",
        fontColor: "#ffffff" 
    },
    'in_active' : {
        title: 'Inactive',
        color: "#F4D03F",
        fontColor: "#ffffff" 
    },
    'deleted' : {
        title: 'Deleted',
        color: "#BE4F59",
        fontColor: "#ffffff" 
    }
}

export const DDSTypes = {
    "provide_reference_numbers": {
        title: 'Provide Reference Numbers'
    },
    "upload_document": {
        title: 'Upload Document'
    },
    "not_obliged": {
        title: 'Not Obliged'
    },
    "in_process": {
        title: 'In Progress'
    },
    "geo_location": {
        title: 'Geolocation'
    }
}

export const DDSActivityTypes = {
    IMPORT: {
        title: 'Import',
        questionnaireFrontId: '4f2fjqu7e52v4slrhctgp8pm84uy9mog'
    },
    EXPORT: {
        title: 'Export',
        questionnaireFrontId: 'vdt1wazwqgu5u1wluwwo9onudqrmox8a'
    },
    DOMESTIC: {
        title: 'Domestic',
        questionnaireFrontId: 'vdt1wazwqgu5u1wl24wo9onudqrmox8a'
    },
    TRADE: {
        title: 'Trade',
        questionnaireFrontId: 'vdt1wazwqgu211wl2r1o9onudqrmox8a'
    }
}

export const DDSStatuses = {
    AVAILABLE: {
        title: 'Available',
        color: 'success',
        icon: <i className="ri-checkbox-fill align-middle me-1" />
    },
    PENDING: {
        title: 'Pending',
        color: 'secondary',
        icon: <i className="ri-time-line align-middle me-1" />
    },
    SUBMITTED: {
        title: 'Submitted',
        color: 'info',
        icon: <i className="ri-upload-line align-middle me-1" />
    },
    VALIDATED: {
        title: 'Validated',
        color: 'success',
        icon: <i className="ri-shield-line align-middle me-1" />
    },
    REJECTED: {
        title: 'Rejected',
        color: 'danger',
        icon: <i className="ri-close-line align-middle me-1" />
    },
    CANCELLED: {
        title: 'Cancelled',
        color: 'warning',
        icon: <i className="ri-forbid-line align-middle me-1" />
    },
    EXPIRED: {
        title: 'Expired',
        color: 'warning',
        icon: <i className="ri-error-warning-line align-middle me-1" />
    },
    NOT_FOUND: {
        title: 'Not Found',
        color: 'danger',
        icon: <i className="ri-question-line align-middle me-1" />
    },
    ERROR: {
        title: 'Error',
        color: 'danger',
        icon: <i className="ri-alert-line align-middle me-1" />
    }
}

export const ShipmentPriorities = {
    low: {
        title: 'Low',
        color: 'success',
    },
    medium: {
        title: 'Medium',
        color: 'warning',
    },
    high: {
        title: 'High',
        color: 'danger',
    },
}

export const GeocledianDeforestationRisks = {
    "low": {
        title: "Low",
        color: "#1CBB8C"
    },
    "medium": {
        title: "Medium",
        color: "#FCC34B"
    },
    "high": {
        title: "High",
        color: "#DC3545"
    }
}

export const GeocledianCountryRisks = {
    "standard": {
        title: "Standard",
        color: "#74788d"
    },
    "low": {
        title: "Low",
        color: "#1CBB8C"
    },
    "medium": {
        title: "Medium",
        color: "#FCC34B"
    },
    "high": {
        title: "High",
        color: "#DC3545"
    }
}

export const KYBCreditTypes = {
    "possible_companies": {
        title: "Possible Companies"
    },
    "kyb_data": {
        title: "KYB Data"
    },
    "sanction_data": {
        title: "Sanction Data"
    },
    "possible_persons": {
        title: "Possible People"
    },
    "monitoring_add_follow_list_item": {
        title: "Monitoring"
    }
}

export const ThirdPartyCategories = {
    company: [
        {
            value: 'supplier',
            label: 'Supplier'
        },
        {
            value: 'customer',
            label: 'Customer'
        },
        {
            value: 'serviceProvider',
            label: 'Service Provider'
        },
        {
            value: 'other',
            label: 'Other'
        }
    ],
    individual: [
        {
            value: 'employee',
            label: 'Employee'
        },
        {
            value: 'freelancer',
            label: 'Freelancer'
        },
        {
            value: 'agent',
            label: 'Agent'
        }, 
        {
            value: 'consultant',
            label: 'Consultant'
        },
        {
            value: 'other',
            label: 'Other'
        }
    ]
}

export const KYBAvailableServices = [
    {
        id: 'dun_brad_street',
        title: "Dun & Bradstreet",
        description: "Dun & Bradstreet provides commercial data, analytics, and insights for businesses.",
        enableFetchReport: true,
        reportTitles: [
            "Executive Summary",
            "D&B Business Key",
            "Corporate Structure & Governance",
            "Key Executives",
            "Financial Overview",
            "Corporate Linkages (Family Tree)",
            "Confidence Score",
            "Branch/Head Office Indication",
            "Trade Styles (DBA/AKA)",
            "Verified Contact Info"
        ],
        searchResultColumns: [
            {
                key: 'name',
                title: 'Company Name'
            },
            {
                key: 'address',
                title: 'Address',
                formatter: (_cell, row) => {
                    return `${row.address}, ${row.city}, ${row.country_code}`
                }
            },
            {
                key: 'postcode',
                title: 'Postal Code'
            },
            {
                key: 'phone_number',
                title: 'Phone Number'
            },
            {
                key: 'match_confidence',
                title: 'Match Confidence'
            }
        ]
    },
    {
        id: 'dun_brad_street_enterprise',
        title: "Dun & Bradstreet Enterprise",
        description: "Dun & Bradstreet provides commercial data, analytics, and insights for businesses.",
        enableFetchReport: false,
        reportTitles: [
            "Executive Summary",
            "D&B Business Key",
            "Corporate Structure & Governance",
            "Key Executives",
            "Financial Overview",
            "Corporate Linkages (Family Tree)",
            "Confidence Score",
            "Branch/Head Office Indication",
            "Trade Styles (DBA/AKA)",
            "Verified Contact Info"
        ],
        searchResultColumns: [
            {
                key: 'name',
                title: 'Company Name'
            },
            {
                key: 'address',
                title: 'Address',
                formatter: (_cell, row) => {
                    return `${row.address}, ${row.city}, ${row.country_code}`
                }
            },
            {
                key: 'postcode',
                title: 'Postal Code'
            },
            {
                key: 'phone_number',
                title: 'Phone Number'
            },
            {
                key: 'match_confidence',
                title: 'Match Confidence'
            }
        ]
    },
    {
        id: 'credit_safe',
        title: "CreditSafe",
        description: "Creditsafe offers credit risk management and business intelligence services.",
        enableFetchReport: true,
        reportTitles: [
            "Executive Summary",
            "Company Identification & Status",
            "Comprehensive Financial Overview",
            "Full Group Structure",
            "Group Structure Graph",
            "Bankers and Advisors",
            "Shareholder & Capital Information",
            "Employee Information",
            "Contact & Management"
        ],
        searchResultColumns: [
            {
                key: 'name',
                title: 'Company Name'
            },
            {
                key: 'address',
                title: 'Address',
                formatter: (_cell, { country, address }) => {
                    return `${address.full_address}, ${country}`
                }
            },
            {
                key: 'registration_number',
                title: 'Registration Number'
            },
            {
                key: 'status',
                title: 'Status'
            },
            {
                key: 'type',
                title: 'Type'
            }
        ]
    },
    {
        id: 'company_info',
        title: "CompanyInfo",
        description: "Company Info provides detailed information about companies.",
        enableFetchReport: true,
        reportTitles: [
            "Executive Summary",
            "Company Identification & Status",
            "Contact Information",
            "Financial Overview",
            "Financial Ratios",
            "Detailed Group Structure",
            "Group Structure Graph",
            "Bankers & Professional Advisors",
            "Employee & Shareholder Information"
        ],
        searchResultColumns: [
            {
                key: 'name',
                title: 'Company Name',
                formatter: (_cell, { attributes }) => {
                    return attributes.name || '-';
                }
            },
            {
                key: 'address',
                title: 'Address',
                formatter: (_cell, { attributes }) => {
                    if (attributes.address) {
                        return `${attributes.address.street} ${attributes.address.houseNumber}, ${attributes.address.postalCode} ${attributes.address.city}, ${attributes.address.country}`
                    } 
                    
                }
            },
            {
                key: 'registration_number',
                title: 'Registration Number',
                formatter: (_cell, { attributes }) => {
                    if (attributes?.identity?.handelsregister) {
                        return `${attributes.identity.handelsregister.register} ${attributes.identity.handelsregister.number}`
                    }

                    return '-'
                }
            },
            {
                key: 'status',
                title: 'Status',
                formatter: (_cell, { attributes }) => {
                    return attributes.status || '-'
                }
            },
            {
                key: 'type',
                title: 'Type'
            }
        ]
    }
];

export const KYBScreeningDataSets = {
    "PEP": {
        "title": "PEP – All",
        "description": "Individuals identified as politically exposed persons, including current, former, and associated roles."
    },
    "PEP-CURRENT": {
        "title": "PEP – Current",
        "description": "Individuals currently holding prominent public or political positions."
    },
    "PEP-FORMER": {
        "title": "PEP – Former",
        "description": "Individuals who previously held prominent public or political positions."
    },
    "PEP-LINKED": {
        "title": "PEP – Associated",
        "description": "Individuals linked to a politically exposed person through family or close association."
    },
    "SAN": {
        "title": "Sanctions – All",
        "description": "Individuals or entities listed on sanctions registers, including current and historical records."
    },
    "SAN-CURRENT": {
        "title": "Sanctions – Current",
        "description": "Individuals or entities currently subject to active sanctions measures."
    },
    "SAN-FORMER": {
        "title": "Sanctions – Former",
        "description": "Individuals or entities previously subject to sanctions measures."
    },
    "INS": {
        "title": "Insolvency",
        "description": "Records indicating insolvency, bankruptcy, or financial distress proceedings."
    },
    "RRE": {
        "title": "Reputational Risk",
        "description": "Adverse media or information that may present reputational risk."
    },
    "DD": {
        "title": "Disqualified Director",
        "description": "Individuals prohibited from acting as a company director or officer."
    },
    "POI": {
        "title": "Profile of Interest",
        "description": "Individuals or entities flagged for monitoring based on risk indicators."
    },
    "REL": {
        "title": "Regulatory Enforcement",
        "description": "Records of regulatory actions, penalties, or enforcement proceedings by authorities."
    },
    "GRI": {
        "title": "Gambling Risk Intelligence.",
        "description": "Gambling Risk Intelligence."
    }
}

export const KYBMonitoringAlertsStatuses = {
    "open": {
        title: "Open",
        description: "Indicates that the alert requires review."
    },
    "true-positive": {
        title: "True Positive",
        description: "Indicates that the alert is closed and that the profile was matched correctly."
    },
    "false-positive": {
        title: "False Positive",
        description: "Indicates that the alert is closed and that the profile was matched incorrectly."
    },
    "discarded": {
        title: "Discarded",
        description: "Indicates that the profile in the alert no longer matches the subscription due to the changes in the profile, subscription, or the follow list."
    }
};

export const KYBPEPTierValues = {
    "PEP Tier 1": {
        title: "PEP Tier 1",
        description: "senior roles"
    },
    "PEP Tier 2": {
        title: "PEP Tier 2",
        description: "middle-ranking"
    },
    "PEP Tier 3": {
        title: "PEP Tier 3",
        description: "junior officials"
    }
}

export const KYBScreeningTypes = {
    "persons": {
        title: "Individual"
    },
    "business": {
        title: "Business"
    }
}

export const KYBMonitoringTypes = {
    "person": {
        title: "Individual"
    },
    "business": {
        title: "Business"
    }
}

export const ComplianceQueryTypes = [
    {
        value: 'screening',
        label: 'Screening'
    },
    {
        value: 'monitoring',
        label: 'Monitoring'
    }
];

export const ComplianceQueryStatuses = [
    {
        value: "start",
        label: "Not Started",
        color: "#6C757D"
    },
    {
        value: "in_progress",
        label: "In Progress",
        color: "#0D6EFD"
    },
    {
        value: "finish",
        label: "Completed",
        color: "#198754"
    }
];

export const ComplianceQueryChoices = [
    {
        value: 'third_party',
        label: 'Third Party',
        relations: [
            {
                value: 'organization',
                label: 'Organization'
            }
        ]
    },
    {
        value: 'management',
        label: 'Management',
        relations: [
            {
                value: 'team_manager',
                label: 'Management Team'
            },
            {
                value: 'kyb_management_member',
                label: 'KYB Management Member'
            }
        ],
        shouldDisplay: ({ thirdpartiesGroupTitle }) => {
            return thirdpartiesGroupTitle !== 'individual'
        }
    },
    {
        value: 'shareholder_individuals',
        label: 'Shareholder (Individuals)',
        relations: [
            {
                value: 'share_holder',
                label: 'Shareholder'
            }
        ]
    },
    {
        value: 'ubo',
        label: 'UBO',
        relations: [
            {
                value: 'share_holder',
                label: 'Shareholder'
            }
        ]
    },
    {
        value: 'shareholder_company',
        label: 'Shareholder (Company)',
        relations: [
            {
                value: 'share_holder',
                label: 'Shareholder'
            }
        ]
    },
    {
        value: 'related_companies',
        label: 'Related Companies (Subsidiary, Group Companies)',
        relations: [
            {
                value: 'organization_legal',
                label: 'Legal Entity'
            }
        ]
    },
    // {
    //     value: 'former_management',
    //     label: 'Former Management'
    // },
    // {
    //     value: 'former_shareholders',
    //     label: 'Former Shareholders'
    // }
];

export const ComplianceQueryItemTypes = [
    {
        value: 'person',
        label: 'Individual'
    },
    {
        value: 'business',
        label: 'Business'
    }
];